import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import api from "../api";

const PALETTE = ["#0f766e", "#2563eb", "#ea580c", "#7c3aed", "#0891b2", "#dc2626"];

const Page = styled.div`
  min-height: 100vh;
  padding: 20px;
  color: #10243f;
  background:
    radial-gradient(circle at top left, rgba(20, 184, 166, 0.14), transparent 32%),
    radial-gradient(circle at top right, rgba(37, 99, 235, 0.12), transparent 28%),
    linear-gradient(180deg, #f4f8fc 0%, #eef4fb 100%);
`;

const Hero = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(280px, 0.8fr);
  gap: 16px;
  margin-bottom: 18px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const HeroPanel = styled.div`
  background: linear-gradient(135deg, #0f172a 0%, #133b5c 56%, #0f766e 100%);
  color: #f8fafc;
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 24px 50px rgba(15, 23, 42, 0.18);
`;

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Title = styled.h2`
  margin: 14px 0 10px;
  font-size: clamp(1.9rem, 3vw, 2.4rem);
  line-height: 1.08;
`;

const Subtitle = styled.p`
  margin: 0;
  max-width: 720px;
  color: rgba(248, 250, 252, 0.82);
  font-size: 0.98rem;
  line-height: 1.6;
`;

const ScopePanel = styled.div`
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 24px;
  padding: 22px;
  box-shadow: 0 16px 38px rgba(15, 23, 42, 0.08);
`;

const ScopeTitle = styled.div`
  color: #0f172a;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 10px;
`;

const ScopeText = styled.p`
  margin: 0 0 16px;
  color: #475569;
  line-height: 1.6;
`;

const ScopeList = styled.div`
  display: grid;
  gap: 10px;
`;

const ScopeItem = styled.div`
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 12px 14px;
`;

const ScopeLabel = styled.div`
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 700;
  margin-bottom: 4px;
`;

const ScopeValue = styled.div`
  color: #10243f;
  font-weight: 800;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(203, 213, 225, 0.72);
  border-radius: 20px;
  padding: 18px;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
`;

const StatGlow = styled.div`
  position: absolute;
  inset: auto -30px -30px auto;
  width: 110px;
  height: 110px;
  border-radius: 999px;
  background: ${(p) => p.$color || "rgba(15, 118, 110, 0.14)"};
  filter: blur(8px);
`;

const StatLabel = styled.div`
  color: #64748b;
  font-size: 0.82rem;
  font-weight: 700;
  margin-bottom: 10px;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 900;
  color: #0f172a;
`;

const StatMeta = styled.div`
  margin-top: 8px;
  color: #475569;
  font-size: 0.86rem;
  line-height: 1.45;
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(320px, 0.85fr);
  gap: 16px;

  @media (max-width: 1150px) {
    grid-template-columns: 1fr;
  }
`;

const ChartColumn = styled.div`
  display: grid;
  gap: 16px;
`;

const SideColumn = styled.div`
  display: grid;
  gap: 16px;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(203, 213, 225, 0.72);
  border-radius: 22px;
  padding: 18px;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
`;

const CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;

const CardTitle = styled.div`
  color: #0f172a;
  font-size: 1.02rem;
  font-weight: 800;
`;

const CardHint = styled.div`
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.5;
`;

const ChartWrap = styled.div`
  width: 100%;
  height: ${(p) => p.$height || 320}px;
`;

const RankList = styled.div`
  display: grid;
  gap: 10px;
`;

const RankRow = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
`;

const RankNo = styled.div`
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: linear-gradient(135deg, #0f766e 0%, #2563eb 100%);
  color: #fff;
  font-weight: 800;
  font-size: 0.9rem;
`;

const RankName = styled.div`
  color: #10243f;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RankCount = styled.div`
  color: #0f172a;
  font-weight: 900;
`;

const FeedList = styled.div`
  display: grid;
  gap: 10px;
`;

const FeedItem = styled.div`
  border-radius: 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 14px;
`;

const FeedTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
`;

const FeedTitle = styled.div`
  color: #10243f;
  font-weight: 800;
`;

const FeedMeta = styled.div`
  margin-top: 6px;
  color: #64748b;
  font-size: 0.84rem;
  line-height: 1.55;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 86px;
  padding: 7px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${(p) => (p.$tone === "warning" ? "#fff7ed" : "#ecfeff")};
  color: ${(p) => (p.$tone === "warning" ? "#c2410c" : "#0f766e")};
  border: 1px solid ${(p) => (p.$tone === "warning" ? "#fdba74" : "#99f6e4")};
`;

const EmptyState = styled.div`
  display: grid;
  place-items: center;
  min-height: 220px;
  color: #64748b;
  text-align: center;
  line-height: 1.6;
`;

const ErrorBox = styled.div`
  margin-bottom: 14px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  font-weight: 700;
  font-size: 0.88rem;
`;

const Skeleton = styled.div`
  height: ${(p) => p.$h || 90}px;
  border-radius: 18px;
  background: linear-gradient(90deg, #e2e8f0 25%, #f8fafc 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

function formatCount(value) {
  return Number(value || 0).toLocaleString("en-IN");
}

function formatDateTime(value) {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AuditDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState({
    today_count: 0,
    last7_count: 0,
    previous7_count: 0,
    active_users_count: 0,
    top_users: [],
    action_distribution: [],
    resource_distribution: [],
    activity_timeline: [],
    recent_activity: [],
  });

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get("/admin/audit-summary");
        if (!active) return;
        setSummary({
          today_count: Number(res?.data?.today_count || 0),
          last7_count: Number(res?.data?.last7_count || 0),
          previous7_count: Number(res?.data?.previous7_count || 0),
          active_users_count: Number(res?.data?.active_users_count || 0),
          top_users: res?.data?.top_users || [],
          action_distribution: res?.data?.action_distribution || [],
          resource_distribution: res?.data?.resource_distribution || [],
          activity_timeline: res?.data?.activity_timeline || [],
          recent_activity: res?.data?.recent_activity || [],
        });
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.message || "Failed to load audit summary.");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, []);

  const deltaText = useMemo(() => {
    const current = Number(summary.last7_count || 0);
    const previous = Number(summary.previous7_count || 0);
    if (!previous && !current) return "No activity in the last two weeks.";
    if (!previous) return "Fresh activity started in the current 7-day window.";
    const diff = current - previous;
    const pct = Math.abs((diff / previous) * 100).toFixed(0);
    if (diff === 0) return "Activity is holding steady compared with the previous 7 days.";
    return diff > 0
      ? `${pct}% higher than the previous 7-day window.`
      : `${pct}% lower than the previous 7-day window.`;
  }, [summary.last7_count, summary.previous7_count]);

  const trackedScope = useMemo(() => {
    const resources = summary.resource_distribution?.length || 0;
    const actions = summary.action_distribution?.length || 0;
    return `${resources} resource groups across ${actions} action types`;
  }, [summary.action_distribution, summary.resource_distribution]);

  return (
    <Page>
      <Hero>
        <HeroPanel>
          <HeroBadge>Organization Audit View</HeroBadge>
          <Title>Audit activity isolated to the currently logged-in organization.</Title>
          <Subtitle>
            This dashboard only surfaces logs created by users, actions, and resources tied to your organization. Cross-organization
            events are excluded so admins can review activity with confidence.
          </Subtitle>
        </HeroPanel>

        <ScopePanel>
          <ScopeTitle>Scope Guardrails</ScopeTitle>
          <ScopeText>
            Audit metrics here are filtered to your organization before charts, tables, and user counts are calculated.
          </ScopeText>
          <ScopeList>
            <ScopeItem>
              <ScopeLabel>Visible Data</ScopeLabel>
              <ScopeValue>Your organization&apos;s audit stream only</ScopeValue>
            </ScopeItem>
            <ScopeItem>
              <ScopeLabel>Top User Window</ScopeLabel>
              <ScopeValue>Last 7 days of org-scoped actions</ScopeValue>
            </ScopeItem>
            <ScopeItem>
              <ScopeLabel>Recent Feed</ScopeLabel>
              <ScopeValue>Latest audited events from the same organization</ScopeValue>
            </ScopeItem>
          </ScopeList>
        </ScopePanel>
      </Hero>

      {error ? <ErrorBox>{error}</ErrorBox> : null}

      <StatsGrid>
        {loading ? (
          <>
            <Skeleton />
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </>
        ) : (
          <>
            <StatCard>
              <StatGlow $color="rgba(20, 184, 166, 0.18)" />
              <StatLabel>Actions Today</StatLabel>
              <StatValue>{formatCount(summary.today_count)}</StatValue>
              <StatMeta>All audited actions created today inside this organization.</StatMeta>
            </StatCard>
            <StatCard>
              <StatGlow $color="rgba(37, 99, 235, 0.18)" />
              <StatLabel>Last 7 Days</StatLabel>
              <StatValue>{formatCount(summary.last7_count)}</StatValue>
              <StatMeta>{deltaText}</StatMeta>
            </StatCard>
            <StatCard>
              <StatGlow $color="rgba(234, 88, 12, 0.16)" />
              <StatLabel>Active Audit Users</StatLabel>
              <StatValue>{formatCount(summary.active_users_count)}</StatValue>
              <StatMeta>Distinct users who generated tracked activity during the current 7-day window.</StatMeta>
            </StatCard>
            <StatCard>
              <StatGlow $color="rgba(124, 58, 237, 0.15)" />
              <StatLabel>Tracked Scope</StatLabel>
              <StatValue style={{ fontSize: "1.1rem", lineHeight: 1.25 }}>{trackedScope}</StatValue>
              <StatMeta>Coverage of the visible audit set for this organization.</StatMeta>
            </StatCard>
          </>
        )}
      </StatsGrid>

      <MainGrid>
        <ChartColumn>
          <Card>
            <CardHead>
              <div>
                <CardTitle>Activity Timeline</CardTitle>
                <CardHint>Seven-day audit trend for this organization.</CardHint>
              </div>
            </CardHead>
            <ChartWrap $height={330}>
              {loading ? (
                <Skeleton $h={320} />
              ) : summary.activity_timeline.length === 0 ? (
                <EmptyState>No activity has been recorded for this organization in the selected time window.</EmptyState>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={summary.activity_timeline}>
                    <CartesianGrid stroke="#dbe7f3" strokeDasharray="4 4" />
                    <XAxis dataKey="label" stroke="#64748b" />
                    <YAxis allowDecimals={false} stroke="#64748b" />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="count"
                      name="Audit Actions"
                      stroke="#0f766e"
                      strokeWidth={3}
                      dot={{ r: 4, fill: "#0f766e" }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </ChartWrap>
          </Card>

          <Card>
            <CardHead>
              <div>
                <CardTitle>Action Distribution</CardTitle>
                <CardHint>Most frequent action types from the last 7 days.</CardHint>
              </div>
            </CardHead>
            <ChartWrap $height={340}>
              {loading ? (
                <Skeleton $h={330} />
              ) : summary.action_distribution.length === 0 ? (
                <EmptyState>No action data is available for this organization yet.</EmptyState>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={summary.action_distribution}>
                    <CartesianGrid stroke="#dbe7f3" strokeDasharray="4 4" />
                    <XAxis dataKey="action" stroke="#64748b" interval={0} angle={-16} textAnchor="end" height={72} />
                    <YAxis allowDecimals={false} stroke="#64748b" />
                    <Tooltip />
                    <Bar dataKey="count" radius={[10, 10, 0, 0]}>
                      {summary.action_distribution.map((entry, index) => (
                        <Cell key={`${entry.action}-${index}`} fill={PALETTE[index % PALETTE.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartWrap>
          </Card>
        </ChartColumn>

        <SideColumn>
          <Card>
            <CardHead>
              <div>
                <CardTitle>Resource Mix</CardTitle>
                <CardHint>Where audit activity is concentrated across resource groups.</CardHint>
              </div>
            </CardHead>
            <ChartWrap $height={280}>
              {loading ? (
                <Skeleton $h={270} />
              ) : summary.resource_distribution.length === 0 ? (
                <EmptyState>No resource breakdown is available yet.</EmptyState>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={summary.resource_distribution}
                      dataKey="count"
                      nameKey="resource_type"
                      innerRadius={56}
                      outerRadius={92}
                      paddingAngle={3}
                    >
                      {summary.resource_distribution.map((entry, index) => (
                        <Cell key={`${entry.resource_type}-${index}`} fill={PALETTE[index % PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartWrap>
          </Card>

          <Card>
            <CardHead>
              <div>
                <CardTitle>Top Users</CardTitle>
                <CardHint>Users generating the most audit events during the last 7 days.</CardHint>
              </div>
            </CardHead>
            {loading ? (
              <>
                <Skeleton $h={60} />
                <Skeleton $h={60} />
                <Skeleton $h={60} />
              </>
            ) : summary.top_users.length === 0 ? (
              <EmptyState>No active audit users for this organization yet.</EmptyState>
            ) : (
              <RankList>
                {summary.top_users.map((user, index) => (
                  <RankRow key={`${user.name}-${index}`}>
                    <RankNo>{index + 1}</RankNo>
                    <RankName>{user.name || "System"}</RankName>
                    <RankCount>{formatCount(user.count)}</RankCount>
                  </RankRow>
                ))}
              </RankList>
            )}
          </Card>

          <Card>
            <CardHead>
              <div>
                <CardTitle>Recent Activity</CardTitle>
                <CardHint>Latest organization-scoped audit events.</CardHint>
              </div>
            </CardHead>
            {loading ? (
              <>
                <Skeleton $h={74} />
                <Skeleton $h={74} />
                <Skeleton $h={74} />
              </>
            ) : summary.recent_activity.length === 0 ? (
              <EmptyState>No recent activity to display.</EmptyState>
            ) : (
              <FeedList>
                {summary.recent_activity.map((item) => (
                  <FeedItem key={item.id}>
                    <FeedTop>
                      <FeedTitle>{item.user_name || "System"}</FeedTitle>
                      <Pill $tone={item.status}>{item.status || "success"}</Pill>
                    </FeedTop>
                    <FeedMeta>
                      {item.action || "unknown_action"} on {item.resource_type || "general"}
                      {item.resource_id ? ` #${item.resource_id}` : ""}{" "}
                      {"\u2022"} {formatDateTime(item.created_at)}
                    </FeedMeta>
                  </FeedItem>
                ))}
              </FeedList>
            )}
          </Card>
        </SideColumn>
      </MainGrid>
    </Page>
  );
}
